import React, { useMemo } from "react";
import {
  Container,
  List,
  ListItem,
  Divider,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";

import Title from "../../ui/Title";
import { useBmapi } from "../../utils/bmapi-context";
import { MANAGER_ROUTES } from "../../utils/constants";

export default function ManageGalleries() {
  const { bmapi } = useBmapi();
  const history = useHistory();

  const userInfo = bmapi?.getUserInfo ? bmapi.getUserInfo() : null;
  const role = userInfo?.role;
  const businessName = userInfo?.business?.name ?? "";
  //const businessType = userInfo?.business?.type ?? "";

  const businessId = bmapi.getUserInfo()?.business?.id;

  const groupedByCode = useMemo(() => {
    // prendo i dati direttamente qui
    const galleriesRaw = bmapi?.settings?.galleries || [];
    let items = galleriesRaw;
    console.log("............", items);

    // Se è annidato come [[...]], prendo il primo livello interno
    if (Array.isArray(items[0])) {
      items = items[0];
    }

    // Map: code -> { businessFlag, sections[] }
    const map = new Map();
    const keyLabels = bmapi.getUserInfo()?.business?.key_labels;
    items.forEach((gallery) => {
      if (!gallery) return;
      const code = gallery.code || "-";
      const sections = Array.isArray(gallery.sections) ? gallery.sections : [];

      const bf = gallery.businessFilter;
      const kl = bf?.key_labels;

      const matchesKeyLabel =
        !!kl?.field_name &&
        kl?.value != null &&
        Array.isArray(keyLabels) &&
        keyLabels.some(
          (k) =>
            k?.key === kl.field_name &&
            Array.isArray(k?.values) &&
            k.values.includes(kl.value)
        );

      if (
        gallery.businessFilter?.business_id === businessId ||
        matchesKeyLabel
      ) {
        if (!map.has(code)) {
          const businessFlag = !!gallery.businessFlag;

          map.set(code, { businessFlag, sections: [] });
        }

        const entry = map.get(code);
        // se ci sono più gallery con stesso code, faccio OR del flag
        //entry.businessFlag = entry.businessFlag || businessFlag;
        if (entry) entry.sections.push(...sections);
      }
    });

    // Risultato: [ [code, { businessFlag, sections[] }], ... ]
    return Array.from(map.entries());
  }, [bmapi, businessId]); // dipendo solo da bmapi

  const handleEdit = (code, sectionName) => {
    const qs = `?code=${encodeURIComponent(code)}&section=${encodeURIComponent(
      sectionName || ""
    )}`;
    history.push(`${MANAGER_ROUTES.GALLERY}${qs}`);
  };

  return (
    <Container maxWidth="md">
      <Title>
        <FormattedMessage
          id="navigation.manager.galleries"
          defaultMessage="Gallerie"
        />
      </Title>

      {/* Intestazione colonne */}
      <Box display="flex" mt={2} mb={1} px={2} style={{ opacity: 0.8 }}>
        <Box flexBasis="20%" flexShrink={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Codice
          </Typography>
        </Box>

        <Box flexBasis="40%" flexShrink={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Contesto
          </Typography>
        </Box>

        <Box flexBasis="20%" flexShrink={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Nome sezione
          </Typography>
        </Box>

        <Box flexBasis="10%" flexShrink={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Tipo sezione
          </Typography>
        </Box>

        <Box flexGrow={1} />
      </Box>

      <Divider />

      <List>
        {groupedByCode.length === 0 && (
          <ListItem>
            <Typography>Nessuna gallery configurata.</Typography>
          </ListItem>
        )}

        {groupedByCode.map(([code, data]) =>
          data.sections.map((section, idx) => {
            const businessFlag = data.businessFlag ?? false;
            const sectionName = section?.name ?? "";
            const sectionType = section?.type ?? "";

            // Se il business è di tipo "Loop" NON mostro le gallery business
            /*if (businessType === "Loop" && businessFlag) {
              return null;
            }*/

            // Se non è business e non sono TM, non mostro la riga
            if (!businessFlag && role !== "TM") {
              return null;
            }

            const contextLabel = businessFlag ? businessName : "Globale";

            return (
              <React.Fragment
                key={`${code}-${sectionName || "section"}-${idx}`}
              >
                <ListItem>
                  <Box display="flex" width="100%" alignItems="center">
                    {/* Colonna 1: CODE (solo prima riga del gruppo) */}
                    <Box
                      flexBasis="20%"
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                    >
                      {idx === 0 && (
                        <Typography variant="body1">{code}</Typography>
                      )}
                    </Box>

                    {/* Colonna 2: CONTESTO (solo prima riga del gruppo) */}
                    <Box
                      flexBasis="40%"
                      flexShrink={0}
                      display="flex"
                      alignItems="center"
                      style={{ overflow: "hidden" }}
                    >
                      {idx === 0 && (
                        <Tooltip title={contextLabel}>
                          <Typography
                            variant="body1"
                            noWrap
                            style={{
                              maxWidth: "100%",
                              textOverflow: "ellipsis",
                            }}
                            tabIndex={0}
                          >
                            {contextLabel}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Colonna 3: NAME */}
                    <Box
                      flexBasis="20%"
                      flexShrink={0}
                      display="flex"
                      alignItems="flex-start"
                    >
                      <Typography variant="body1">{sectionName}</Typography>
                    </Box>

                    {/* Colonna 4: TYPE */}
                    <Box
                      flexBasis="10%"
                      flexShrink={0}
                      display="flex"
                      alignItems="flex-start"
                    >
                      <Typography variant="body1">{sectionType}</Typography>
                    </Box>

                    {/* Icona Edit a destra */}
                    <Box flexGrow={1} display="flex" justifyContent="flex-end">
                      <IconButton
                        aria-label="edit"
                        onClick={() => handleEdit(code, sectionName)}
                      >
                        <Edit />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            );
          })
        )}
      </List>
    </Container>
  );
}
